import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Zap, Flame, Droplets, Save, TrendingUp, Wrench, Trash2, AlertTriangle, CheckCircle2, Pencil, ClipboardList, ClipboardCheck } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { fmtTimeIST } from '../lib/tz';
import ManagerMaintenanceQueue from './ManagerMaintenanceQueue';
import { ManagerDailyReadingsFill } from './DailyReadings';


const API = process.env.REACT_APP_BACKEND_URL + '/api';
const METER_TYPES = [
  { key: 'electricity', label: 'Electricity', icon: Zap, unit: 'kWh', color: '#D4A017' },
  { key: 'gas', label: 'Gas / Fuel', icon: Flame, unit: 'm3', color: '#C45A41' },
  { key: 'water', label: 'Water', icon: Droplets, unit: 'kL', color: '#2D6A4F' }
];
const fmtCurrency = v => v != null ? `₹${Math.round(Number(v)).toLocaleString('en-IN')}` : '—';
const fmtNum = v => v != null ? Math.round(Number(v)).toLocaleString('en-IN') : '—';
const EXPENSE_NATURES = ["AMC", "Equipment breakdown", "Paint & touch-up", "Plumbing", "Electrical", "Labour", "Others"];

export default function ManagerMaintenance() {
  const { getHeaders } = useAuth();
  const [selectedDate, setSelectedDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 1); return d; });
  const [readings, setReadings] = useState({});
  const [prevClosings, setPrevClosings] = useState({});
  const [mtdData, setMtdData] = useState(null);
  const [saving, setSaving] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [expenseForm, setExpenseForm] = useState({ nature: '', description: '', amount: '', date: format(new Date(), 'yyyy-MM-dd') });
  const [savingExpense, setSavingExpense] = useState(false);
  const [activeTab, setActiveTab] = useState('queue');
  const [editingMeter, setEditingMeter] = useState(null);
  const [prevClosingDates, setPrevClosingDates] = useState({});
  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  const [eligibleMeters, setEligibleMeters] = useState([]);

  const loadReadings = useCallback(async () => {
    try {
      const [res, elig] = await Promise.all([
        axios.get(`${API}/maintenance/readings?date=${dateStr}`, { headers: getHeaders() }),
        axios.get(`${API}/maintenance/eligible-meters?date=${dateStr}`, { headers: getHeaders() }),
      ]);
      const map = {};
      for (const r of (res.data.readings || res.data)) {
        // Key by meter_id (canonical); fallback to meter_type for legacy single-meter records
        map[r.meter_id || r.meter_type] = r;
      }
      setReadings(map);
      setPrevClosings(res.data.prev_closings || {});
      setPrevClosingDates(res.data.prev_closing_dates || {});
      setEligibleMeters(elig.data.meters || []);
      setEditingMeter(null);
    } catch (err) { console.error('Failed to load readings:', err); }
  }, [dateStr, getHeaders]);

  const loadMTD = useCallback(async () => {
    try {
      const y = selectedDate.getFullYear();
      const m = selectedDate.getMonth() + 1;
      const res = await axios.get(`${API}/maintenance/mtd?year=${y}&month=${m}`, { headers: getHeaders() });
      setMtdData(res.data);
    } catch (err) { console.error('Failed to load MTD:', err); }
  }, [selectedDate, getHeaders]);

  useEffect(() => { loadReadings(); loadMTD(); }, [loadReadings, loadMTD]);

  const loadExpenses = useCallback(async () => {
    try {
      const y = selectedDate.getFullYear();
      const m = selectedDate.getMonth() + 1;
      const res = await axios.get(`${API}/maintenance/expenses?year=${y}&month=${m}`, { headers: getHeaders() });
      setExpenses(res.data.expenses || []);
    } catch (err) { console.error('Failed to load expenses:', err); }
  }, [selectedDate, getHeaders]);

  useEffect(() => { loadExpenses(); }, [loadExpenses]);

  const saveExpense = async () => {
    if (!expenseForm.nature) { toast.error('Select expense nature'); return; }
    if (!expenseForm.amount || parseFloat(expenseForm.amount) <= 0) { toast.error('Enter valid amount'); return; }
    if (!expenseForm.date) { toast.error('Select date'); return; }
    setSavingExpense(true);
    try {
      await axios.post(`${API}/maintenance/expenses`, {
        nature: expenseForm.nature,
        description: expenseForm.description,
        amount: parseFloat(expenseForm.amount),
        date: expenseForm.date
      }, { headers: getHeaders() });
      toast.success('Expense added');
      setExpenseForm({ nature: '', description: '', amount: '', date: format(new Date(), 'yyyy-MM-dd') });
      loadExpenses();
      loadMTD();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save expense');
    } finally { setSavingExpense(false); }
  };

  const deleteExpense = async (id) => {
    try {
      await axios.delete(`${API}/maintenance/expenses/${id}`, { headers: getHeaders() });
      toast.success('Expense deleted');
      loadExpenses();
      loadMTD();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const [forms, setForms] = useState({});

  useEffect(() => {
    const newForms = {};
    for (const em of eligibleMeters) {
      const r = readings[em.meter_id];
      if (em.reading_mode === 'consumption_only') {
        newForms[em.meter_id] = r ? { consumption: String(r.consumption || '') } : { consumption: '' };
      } else {
        const autoOpening = em.previous_closing != null ? String(em.previous_closing) : '';
        newForms[em.meter_id] = r
          ? { opening: String(r.opening || ''), closing: String(r.closing || '') }
          : { opening: autoOpening, closing: '' };
      }
    }
    setForms(newForms);
  }, [readings, eligibleMeters]);

  const saveReading = async (meterId) => {
    const em = eligibleMeters.find(x => x.meter_id === meterId);
    if (!em) return;
    const f = forms[meterId] || {};
    let payload;
    if (em.reading_mode === 'consumption_only') {
      const consumption = parseFloat(f.consumption) || 0;
      if (consumption < 0) { toast.error('Consumption cannot be negative'); return; }
      payload = { date: dateStr, meter_type: em.meter_type, meter_id: meterId, consumption };
    } else {
      const opening = parseFloat(f.opening) || 0;
      const closing = parseFloat(f.closing) || 0;
      if (closing < opening) { toast.error('Closing reading must be >= Opening reading'); return; }
      payload = { date: dateStr, meter_type: em.meter_type, meter_id: meterId, opening, closing };
    }
    setSaving(meterId);
    try {
      const res = await axios.post(`${API}/maintenance/readings`, payload, { headers: getHeaders() });
      toast.success(`${em.name} saved — Consumption: ${res.data.consumption}, Cost: ₹${Math.round(res.data.cost || 0).toLocaleString('en-IN')}`);
      loadReadings();
      loadMTD();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(''); }
  };

  const bt = mtdData?.budget_tracking;

  return (
    <div className="w-full" data-testid="manager-maintenance-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-[#1A2421]">Maintenance & Energy</h1>
          <p className="text-sm text-[#5C6B64] mt-1">Daily meter readings, expenses & budget tracking</p>
        </div>
        {activeTab !== 'readings' && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm"
              onClick={() => { const y = new Date(); y.setDate(y.getDate() - 1); setSelectedDate(y); }}
              className={`border-[#E2E8DF] text-[#5C6B64] hover:bg-[#F3F5F2] h-10 ${
                format(selectedDate, 'yyyy-MM-dd') === format((() => { const y = new Date(); y.setDate(y.getDate() - 1); return y; })(), 'yyyy-MM-dd')
                  ? 'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)]' : ''
              }`}
              data-testid="maint-yesterday-btn">
              Yesterday
            </Button>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-10 border-[#E2E8DF] text-[#1A2421]" data-testid="maint-date-picker">
                {format(selectedDate, 'dd MMM yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={selectedDate}
                onSelect={d => { if (d) { setSelectedDate(d); setCalendarOpen(false); } }}
                disabled={d => d > new Date()} />
            </PopoverContent>
          </Popover>
          </div>
        )}
      </div>

      {/* Budget Tracking Card — hidden on Daily Readings (equipment health, not utility spend). */}
      {bt && activeTab !== 'readings' && (
        <Card className={`mb-6 border-[#E2E8DF] shadow-sm ${bt.budget > 0 && bt.pct_used >= 100 ? 'border-[#C45A41]/40 bg-[#C45A41]/3' : bt.budget > 0 && bt.pct_used >= 90 ? 'border-[#C45A41]/20' : ''}`} data-testid="budget-tracking-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Wrench className="w-4 h-4 text-[#5C6B64]" />
              <span className="text-sm font-semibold text-[#1A2421]">Monthly Budget Tracker</span>
              {bt.budget > 0 && bt.pct_used >= 100 && (
                <span className="flex items-center gap-1 text-xs text-[#C45A41] font-medium ml-auto" data-testid="budget-exceeded-alert">
                  <AlertTriangle className="w-3.5 h-3.5" />Budget Exceeded
                </span>
              )}
              {bt.budget > 0 && bt.pct_used >= 90 && bt.pct_used < 100 && (
                <span className="flex items-center gap-1 text-xs text-[#C45A41] font-medium ml-auto" data-testid="budget-critical-alert">
                  <AlertTriangle className="w-3.5 h-3.5" />Critical — {bt.pct_used}% used
                </span>
              )}
              {bt.budget > 0 && bt.pct_used >= 75 && bt.pct_used < 90 && (
                <span className="flex items-center gap-1 text-xs text-[#D4A017] font-medium ml-auto" data-testid="budget-warning-alert">
                  <AlertTriangle className="w-3.5 h-3.5" />Warning — {bt.pct_used}% used
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 bg-[#F9F9F8] rounded-lg text-center">
                <p className="text-[10px] text-[#5C6B64] uppercase tracking-wide">Budget</p>
                <p className="text-lg font-bold text-[#1A2421]" data-testid="budget-amount">{bt.budget > 0 ? fmtCurrency(bt.budget) : 'Not Set'}</p>
              </div>
              <div className="p-3 bg-[#F9F9F8] rounded-lg text-center">
                <p className="text-[10px] text-[#5C6B64] uppercase tracking-wide">Spent (MTD)</p>
                <p className="text-lg font-bold text-[#1A2421]" data-testid="budget-spent">{fmtCurrency(bt.total_spend)}</p>
                <p className="text-[10px] text-[#5C6B64]">Energy {fmtCurrency(bt.energy_cost)} + Expenses {fmtCurrency(bt.expenses_cost)}</p>
              </div>
              <div className="p-3 bg-[#F9F9F8] rounded-lg text-center">
                <p className="text-[10px] text-[#5C6B64] uppercase tracking-wide">Remaining</p>
                <p className={`text-lg font-bold ${bt.remaining < 0 ? 'text-[#C45A41]' : 'text-[#2D6A4F]'}`} data-testid="budget-remaining">
                  {bt.budget > 0 ? fmtCurrency(bt.remaining) : '—'}
                </p>
              </div>
              <div className="p-3 bg-[#F9F9F8] rounded-lg text-center">
                <p className="text-[10px] text-[#5C6B64] uppercase tracking-wide">% Used</p>
                <p className={`text-lg font-bold ${bt.pct_used > 100 ? 'text-[#C45A41]' : bt.pct_used > 80 ? 'text-[#D4A017]' : 'text-[#2D6A4F]'}`} data-testid="budget-pct-used">
                  {bt.budget > 0 ? `${bt.pct_used}%` : '—'}
                </p>
              </div>
            </div>
            {bt.budget > 0 && (
              <div className="mt-3 h-2 bg-[#E2E8DF] rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${bt.pct_used >= 100 ? 'bg-[#C45A41]' : bt.pct_used >= 90 ? 'bg-[#C45A41]' : bt.pct_used >= 75 ? 'bg-[#D4A017]' : 'bg-[#2D6A4F]'}`}
                  style={{ width: `${Math.min(bt.pct_used, 100)}%` }} />
              </div>
            )}
            {bt.budget > 0 && bt.total_spend > 0 && (() => {
              const today = new Date();
              const dayOfMonth = today.getDate();
              const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
              const projected = dayOfMonth > 0 ? Math.round(bt.total_spend / dayOfMonth * daysInMonth) : 0;
              if (projected > bt.budget) {
                return (
                  <p className="text-xs text-[#C45A41] mt-2 flex items-center gap-1" data-testid="budget-projected-alert">
                    <AlertTriangle className="w-3 h-3 shrink-0" />
                    Projected spend: {fmtCurrency(projected)} — exceeds budget by {fmtCurrency(projected - bt.budget)}
                  </p>
                );
              }
              return null;
            })()}
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4 bg-[#F3F5F2] flex w-full max-w-full overflow-x-auto">
          <TabsTrigger value="queue" data-testid="tab-queue" className="shrink-0">
            <ClipboardList className="w-3.5 h-3.5 mr-1" /> Work Queue
          </TabsTrigger>
          <TabsTrigger value="readings" data-testid="tab-daily-readings" className="shrink-0">
            <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Daily Readings
          </TabsTrigger>
          <TabsTrigger value="readings-meter" data-testid="tab-readings" className="shrink-0">Meter Readings</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses" className="shrink-0">Maintenance Expenses</TabsTrigger>
        </TabsList>

        <TabsContent value="queue">
          <ManagerMaintenanceQueue />
        </TabsContent>

        <TabsContent value="readings">
          <ManagerDailyReadingsFill />
        </TabsContent>

        <TabsContent value="readings-meter">
      {/* Meter Reading Cards — one card per PHYSICAL meter (Chunk 3) */}
      <div className="space-y-6" data-testid="meter-readings-list">
        {['electricity', 'gas', 'water'].map(mt => {
          const label = mt === 'electricity' ? 'Electricity' : mt === 'gas' ? 'Gas / Fuel' : 'Water';
          const Icon = mt === 'electricity' ? Zap : mt === 'gas' ? Flame : Droplets;
          const color = mt === 'electricity' ? '#D4A017' : mt === 'gas' ? '#C45A41' : '#2D6A4F';
          const metersOfType = eligibleMeters.filter(m => m.meter_type === mt);
          const submittedCount = metersOfType.filter(m => readings[m.meter_id]).length;
          const totalCount = metersOfType.length;
          if (totalCount === 0) return null;
          const typeMtd = mtdData?.meters?.[mt];
          return (
            <div key={mt} className="space-y-3" data-testid={`meter-group-${mt}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="w-5 h-5" style={{ color }} />
                  <h3 className="text-base font-semibold text-[#1A2421]">{label}</h3>
                  {totalCount > 0 && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${submittedCount === totalCount ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#D4A017]/10 text-[#D4A017]'}`} data-testid={`${mt}-completeness`}>
                      {submittedCount} of {totalCount} completed
                    </span>
                  )}
                </div>
                {typeMtd && typeMtd.days_with_data > 0 && (
                  <div className="text-right">
                    <p className="text-[10px] text-[#5C6B64] uppercase">MTD Actual · Expected</p>
                    <p className="text-sm font-semibold text-[#1A2421]" data-testid={`${mt}-type-summary`}>
                      {fmtCurrency(typeMtd.total_cost)} · {fmtCurrency(typeMtd.expected_bill)}
                    </p>
                  </div>
                )}
              </div>
              {metersOfType.map(em => {
                const existing = readings[em.meter_id];
                const f = forms[em.meter_id] || { opening: '', closing: '', consumption: '' };
                const isConsOnly = em.reading_mode === 'consumption_only';
                const opening = parseFloat(f.opening) || 0;
                const closing = parseFloat(f.closing) || 0;
                const consFromReadings = isConsOnly ? (parseFloat(f.consumption) || 0) : (closing >= opening ? closing - opening : 0);
                const meterInvalid = !isConsOnly && closing > 0 && opening > 0 && closing < opening;
                const isLocked = !!existing && editingMeter !== em.meter_id;
                const savedTime = existing?.updated_at ? fmtTimeIST(existing.updated_at) : '';
                const rate = em.resolved_rate || 0;
                return (
                  <Card key={em.meter_id} className="border-[#E2E8DF] shadow-sm" data-testid={`meter-card-${em.meter_id}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2 flex-wrap">
                        <span>{em.name}</span>
                        {em.meter_number && <span className="text-xs text-[#5C6B64] font-normal">{em.meter_number}</span>}
                        <span className="text-xs text-[#5C6B64] font-normal">· ₹{rate}/{em.unit}</span>
                        {existing && (
                          <span className="flex items-center gap-1 text-xs font-normal text-[#2D6A4F] bg-[#2D6A4F]/10 px-2 py-0.5 rounded-full" data-testid={`${em.meter_id}-saved-badge`}>
                            <CheckCircle2 className="w-3 h-3" />Saved{savedTime ? ` at ${savedTime}` : ''}
                          </span>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-3">
                      {isConsOnly ? (
                        <div className="space-y-1.5">
                          <Label className="text-xs text-[#5C6B64]">Consumption ({em.unit})</Label>
                          <Input type="number" inputMode="decimal" step="any" placeholder="0"
                            value={f.consumption || ''}
                            onChange={e => setForms(prev => ({ ...prev, [em.meter_id]: { consumption: e.target.value } }))}
                            className="h-10 border-[#E2E8DF]"
                            readOnly={isLocked} disabled={isLocked}
                            data-testid={`${em.meter_id}-consumption-input`} />
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3 items-start">
                          <div className="space-y-1.5">
                            <Label className="text-xs text-[#5C6B64]">Opening Reading</Label>
                            {(() => {
                              const openingLocked = isLocked || existing || em.previous_closing != null;
                              return (
                                <>
                                  <Input type="number" inputMode="decimal" step="any" placeholder="0"
                                    value={f.opening}
                                    onChange={e => !openingLocked && setForms(prev => ({ ...prev, [em.meter_id]: { ...prev[em.meter_id], opening: e.target.value } }))}
                                    readOnly={openingLocked}
                                    className={`h-10 border-[#E2E8DF] ${openingLocked ? 'bg-[#EEEEE9] cursor-not-allowed opacity-70' : ''}`}
                                    data-testid={`${em.meter_id}-opening-input`} />
                                  {!existing && em.previous_closing != null && em.previous_closing_date && (
                                    <p className="text-[10px] text-[#2D6A4F] font-medium" data-testid={`${em.meter_id}-autofill-label`}>
                                      Auto-filled from {em.previous_closing_date}
                                    </p>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs text-[#5C6B64]">Closing Reading</Label>
                            <Input type="number" inputMode="decimal" step="any" placeholder="0"
                              value={f.closing}
                              onChange={e => !isLocked && setForms(prev => ({ ...prev, [em.meter_id]: { ...prev[em.meter_id], closing: e.target.value } }))}
                              readOnly={isLocked} disabled={isLocked}
                              className={`h-10 border-[#E2E8DF] ${isLocked ? 'bg-[#EEEEE9] cursor-not-allowed opacity-70' : ''}`}
                              data-testid={`${em.meter_id}-closing-input`} />
                          </div>
                        </div>
                      )}
                      <div className={`flex items-center justify-between p-2.5 rounded-lg ${meterInvalid ? 'bg-[#C45A41]/5 border border-[#C45A41]/20' : 'bg-[#F3F5F2]'}`}>
                        <div>
                          <span className="text-xs text-[#5C6B64]">Consumption</span>
                          <p className={`text-base font-semibold ${meterInvalid ? 'text-[#C45A41]' : 'text-[#1A2421]'}`} data-testid={`${em.meter_id}-consumption`}>
                            {meterInvalid ? 'Invalid' : consFromReadings > 0 ? `${fmtNum(consFromReadings)} ${em.unit}` : '—'}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-[#5C6B64]">Est. Cost</span>
                          <p className="text-base font-semibold text-[#1A2421]" data-testid={`${em.meter_id}-cost`}>
                            {consFromReadings > 0 && rate > 0 ? fmtCurrency(consFromReadings * rate) : '—'}
                          </p>
                        </div>
                      </div>
                      {isLocked ? (
                        <Button onClick={() => setEditingMeter(em.meter_id)}
                          variant="outline"
                          className="w-full h-10 border-[#D4A017] text-[#D4A017] hover:bg-[#D4A017]/10 text-sm"
                          data-testid={`edit-${em.meter_id}-btn`}>
                          <Pencil className="w-3.5 h-3.5 mr-1.5" />Edit Reading
                        </Button>
                      ) : (
                        <Button onClick={() => saveReading(em.meter_id)} disabled={saving === em.meter_id || meterInvalid}
                          className="w-full h-10 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white text-sm"
                          data-testid={`save-${em.meter_id}-btn`}>
                          <Save className="w-3.5 h-3.5 mr-1.5" />
                          {saving === em.meter_id ? 'Saving...' : meterInvalid ? 'Fix reading error' : existing ? 'Save Edit' : 'Save Reading'}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          );
        })}
        {eligibleMeters.length === 0 && (
          <p className="text-sm text-[#5C6B64] italic text-center py-6">No meters configured for this date. Contact your Admin.</p>
        )}
      </div>
        </TabsContent>

        <TabsContent value="expenses">
          {/* Add Expense Form */}
          <Card className="border-[#E2E8DF] shadow-sm mb-6" data-testid="add-expense-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#1A2421] flex items-center gap-2">
                <Wrench className="w-4 h-4 text-[#5C6B64]" />
                Add Maintenance Expense
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#5C6B64]">Nature</Label>
                  <Select value={expenseForm.nature} onValueChange={v => setExpenseForm(p => ({ ...p, nature: v }))}>
                    <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="expense-nature-select">
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPENSE_NATURES.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#5C6B64]">Date</Label>
                  <Input type="date" value={expenseForm.date}
                    onChange={e => setExpenseForm(p => ({ ...p, date: e.target.value }))}
                    className="h-10 border-[#E2E8DF]" data-testid="expense-date-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5C6B64]">Description</Label>
                <Input placeholder="Brief description..." value={expenseForm.description}
                  onChange={e => setExpenseForm(p => ({ ...p, description: e.target.value }))}
                  className="h-10 border-[#E2E8DF]" data-testid="expense-desc-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5C6B64]">Amount (₹)</Label>
                <Input type="number" placeholder="0" value={expenseForm.amount}
                  onChange={e => setExpenseForm(p => ({ ...p, amount: e.target.value }))}
                  className="h-10 border-[#E2E8DF]" data-testid="expense-amount-input" />
              </div>
              <Button onClick={saveExpense} disabled={savingExpense}
                className="w-full h-10 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white text-sm"
                data-testid="save-expense-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" />
                {savingExpense ? 'Saving...' : 'Add Expense'}
              </Button>
            </CardContent>
          </Card>

          {/* Expenses History */}
          <Card className="border-[#E2E8DF] shadow-sm" data-testid="expenses-list-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#1A2421]">
                Expenses — {format(selectedDate, 'MMMM yyyy')}
                {expenses.length > 0 && <span className="text-sm font-normal text-[#5C6B64] ml-2">({expenses.length} entries)</span>}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {expenses.length === 0 ? (
                <p className="text-sm text-[#5C6B64] py-6 text-center">No maintenance expenses this month</p>
              ) : (
                <div className="space-y-2">
                  {expenses.map(e => (
                    <div key={e.id} className="flex items-center gap-3 p-3 bg-[#F9F9F8] rounded-lg border border-[#E2E8DF]" data-testid={`expense-row-${e.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-[color:var(--color-primary)] bg-[color:var(--color-primary)]/8 px-2 py-0.5 rounded">{e.nature}</span>
                          <span className="text-xs text-[#5C6B64]">{e.date}</span>
                        </div>
                        {e.description && <p className="text-sm text-[#5C6B64] mt-1 truncate">{e.description}</p>}
                      </div>
                      <span className="text-sm font-semibold text-[#1A2421] whitespace-nowrap">{fmtCurrency(e.amount)}</span>
                      <Button variant="ghost" size="sm" onClick={() => deleteExpense(e.id)}
                        className="h-8 w-8 p-0 text-[#C45A41] hover:text-[#C45A41] hover:bg-[#C45A41]/10"
                        data-testid={`delete-expense-${e.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2 border-t border-[#E2E8DF]">
                    <span className="text-sm font-semibold text-[#1A2421]">Total: {fmtCurrency(expenses.reduce((s, e) => s + e.amount, 0))}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
